import { useCallback, useState } from 'react';
import { useAuthenticatedApiContext } from '../api/useApiClient';
import { DownloadableLicense } from '../types';
import { AsyncState, useAsync } from './useAsync';
import { useAsyncEffect } from './useAsyncEffect';

interface DownloadableLicensesHook {
  downloadableLicenses: DownloadableLicense[];
  loadDownloadableLicenses: AsyncState<void, () => Promise<void>>;
  downloadLicense: (licenseId: number) => Promise<Blob>;
  deleteLicense: (licenseId: number) => Promise<void>;
}

/**
 * Loads the on-prem licenses a tenant can download — pending offers plus licenses on already linked
 * instances, which stay available for re-download.
 */
export const useDownloadableLicenses = (): DownloadableLicensesHook => {
  const [downloadableLicenses, setDownloadableLicenses] = useState<DownloadableLicense[]>([]);
  const apiClient = useAuthenticatedApiContext();

  const loadDownloadableLicenses = useAsync(
    useCallback((): Promise<void> => {
      // The service already orders available (actionable) rows first, then newest offered.
      return apiClient.getDownloadableLicenses().then((res) => {
        setDownloadableLicenses(res);
      });
    }, [apiClient]),
    { autoReset: true }
  );
  useAsyncEffect(loadDownloadableLicenses, []);

  // Downloading a pending license promotes its available_instance to a linked instance, so the list
  // is reloaded to pick up the new instance details and download timestamp.
  const downloadLicense = useCallback(
    async (licenseId: number): Promise<Blob> => {
      const blob = await apiClient.downloadFile(`/v1/licenses/${licenseId}/download`);
      await loadDownloadableLicenses.execute();
      return blob;
    },
    [apiClient, loadDownloadableLicenses]
  );

  // Removing a license drops its row, so the list is reloaded rather than patched locally: the
  // service is the authority on what is still offered.
  const deleteLicense = useCallback(
    async (licenseId: number): Promise<void> => {
      await apiClient.deleteDownloadableLicense(licenseId);
      await loadDownloadableLicenses.execute();
    },
    [apiClient, loadDownloadableLicenses]
  );

  return {
    downloadableLicenses,
    loadDownloadableLicenses,
    downloadLicense,
    deleteLicense,
  };
};
